import { useZero } from "@rocicorp/zero/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useMouseDownOutside } from "../../../hooks/useMouseDownOutside";
import { useQuery } from "../../../hooks/useQuery";
import { playClick } from "../../../lib/sounds";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Avatar } from "../../Avatar";
import { Button } from "../../Button";
import { Dropdown } from "../../Dropdown";
import { Icon } from "../../Icon";
import { ListTable } from "../../ListTable";
import { Modal } from "../../Modal";
import {
  HeaderRight,
  SettingsModalBody,
  SettingsModalFooter,
  SettingsModalHeader,
} from "../../SettingsLayout";
import { Text } from "../../Text";
import { makeUrl, routes } from "../../../lib/urls";
import {
  AnchoredListPopover,
  Card,
  ChipRemove,
  ColoredIconBadge,
  HoverActions,
  ListBox,
  MatchRow,
  MatchSectionLabel,
  PendingChip,
  PlainInput,
  SearchBar,
  SearchIconWrap,
  SearchInputWrap,
  SectionHeading,
  settingsModalStyle,
} from "../shared";

type PendingUser = { id: string; name: string };

type Props = {
  onClose: () => void;
};

export function TenantAdminSettings({ onClose }: Props) {
  const z = useZero();
  const [allUsers] = useQuery(queries.allUsers());
  const [adminGrants] = useQuery(queries.tenantAdminGrants());

  const activeUsers = useMemo(
    () => (allUsers ?? []).filter((u) => !u.deactivated),
    [allUsers]
  );

  const memberUserIds = useMemo(
    () =>
      new Set(adminGrants.map((g) => g.user?.id).filter(Boolean) as string[]),
    [adminGrants]
  );

  const activeAdminCount = useMemo(
    () => adminGrants.filter((g) => !g.user?.deactivated).length,
    [adminGrants]
  );

  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<PendingUser | null>(null);
  const [showMatches, setShowMatches] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [searchBarRef, matchesRef] = useMouseDownOutside(() =>
    setShowMatches(false)
  );

  const userMatches = useMemo(() => {
    const lower = search.trim().toLowerCase();
    return activeUsers
      .filter((u) => !memberUserIds.has(u.id) && u.id !== pending?.id)
      .filter((u) => {
        if (!lower) return true;
        return (
          userDisplayName(u).toLowerCase().includes(lower) ||
          u.email.toLowerCase().includes(lower)
        );
      });
  }, [activeUsers, memberUserIds, pending, search]);

  const resetPending = useCallback(() => {
    setPending(null);
    setSearch("");
    setShowMatches(false);
  }, []);

  const handleAddMember = useCallback(() => {
    if (!pending) return;
    z.mutate(
      sharedMutators.setTenantAdmins({
        userIds: [...memberUserIds, pending.id],
      })
    );
    resetPending();
  }, [memberUserIds, pending, resetPending, z]);

  const handleRemoveMember = useCallback(
    (userId: string) => {
      const remaining = [...memberUserIds].filter((id) => id !== userId);
      if (remaining.length === 0) return;
      z.mutate(sharedMutators.setTenantAdmins({ userIds: remaining }));
    },
    [memberUserIds, z]
  );

  return (
    <Modal open onClose={onClose} style={settingsModalStyle}>
      <SettingsModalHeader>
        <ColoredIconBadge id="__tenant-admins__" iconName="building-2" />
        <Text variant="text-regular-md">Tenant admins</Text>
        <HeaderRight>
          <Button size="l" variant="secondary" icon="x" onClick={onClose} />
        </HeaderRight>
      </SettingsModalHeader>

      <SettingsModalBody style={{ gap: 24 }} data-popover-host>
        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Members</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Tenant admins have full access to everything.
            </Text>
          </SectionHeading>

          <SearchBar
            ref={searchBarRef}
            onMouseDown={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest("button, input, [data-no-focus-steal]"))
                return;
              e.preventDefault();
              searchInputRef.current?.focus();
            }}
          >
            <SearchInputWrap>
              <SearchIconWrap>
                <Icon name="search" size={16} />
              </SearchIconWrap>
              {pending ? (
                <PendingChip>
                  <Icon name="user-round-check" size={12} />
                  <Text variant="text-small" ellipsis style={{ maxWidth: 160 }}>
                    {pending.name}
                  </Text>
                  <ChipRemove onPress={resetPending}>
                    <Icon name="x" size={12} />
                  </ChipRemove>
                </PendingChip>
              ) : (
                <PlainInput
                  ref={searchInputRef}
                  placeholder="Search members…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setShowMatches(true);
                  }}
                  onFocus={() => setShowMatches(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowMatches(false);
                      setSearch("");
                    } else if (
                      e.key === "Enter" &&
                      userMatches.length > 0 &&
                      showMatches
                    ) {
                      e.preventDefault();
                      const match = userMatches[0];
                      if (match) {
                        setPending({
                          id: match.id,
                          name: userDisplayName(match),
                        });
                        setSearch("");
                        setShowMatches(false);
                      }
                    }
                  }}
                />
              )}
            </SearchInputWrap>
            <Button
              variant="primary"
              disabled={!pending}
              onClick={handleAddMember}
            >
              Add
            </Button>
            <AnchoredListPopover
              anchorRef={searchBarRef}
              open={showMatches && !pending && userMatches.length > 0}
              popoverRef={matchesRef}
            >
              <MatchSectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  Members
                </Text>
              </MatchSectionLabel>
              {userMatches.map((u) => (
                <MatchRow
                  key={u.id}
                  type="button"
                  onMouseDown={() => playClick()}
                  onClick={() => {
                    setPending({ id: u.id, name: userDisplayName(u) });
                    setSearch("");
                    setShowMatches(false);
                  }}
                >
                  <Avatar user={u} size={20} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {userDisplayName(u)}
                    </Text>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {u.email}
                    </Text>
                  </div>
                </MatchRow>
              ))}
            </AnchoredListPopover>
          </SearchBar>

          <ListBox data-compact="true">
            <ListTable
              headers={{ name: "Name", actions: "" }}
              items={adminGrants.flatMap((g) => {
                const u = g.user;
                if (!u || u.deactivated) return [];
                return [
                  {
                    key: g.id,
                    link: makeUrl(routes.modal.member, { id: u.id }),
                    name: (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          minWidth: 0,
                        }}
                      >
                        <Avatar user={u} size={32} />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            minWidth: 0,
                          }}
                        >
                          <Text
                            ellipsis
                            variant="text-regular-md"
                            color="var(--text-primary)"
                          >
                            {userDisplayName(u)}
                          </Text>
                          <Text
                            ellipsis
                            variant="text-small"
                            color="var(--text-secondary)"
                          >
                            {u.email}
                          </Text>
                        </div>
                      </div>
                    ),
                    actions: (hasMouse: boolean) => (
                      <HoverActions visible={hasMouse}>
                        <Dropdown
                          opener={
                            <Button
                              variant="secondary"
                              icon="more"
                              disabled={activeAdminCount <= 1}
                            />
                          }
                          align="right"
                          minWidth={160}
                          sections={[
                            {
                              items: [
                                {
                                  type: "button" as const,
                                  left: <Icon name="trash" size={16} />,
                                  label: "Remove member",
                                  onClick: () => handleRemoveMember(u.id),
                                },
                              ],
                            },
                          ]}
                        />
                      </HoverActions>
                    ),
                  },
                ];
              })}
              emptyState={null}
            />
          </ListBox>
        </Card>
      </SettingsModalBody>

      <SettingsModalFooter style={{ gap: 8 }}>
        <Button size="l" onClick={onClose}>
          Done
        </Button>
      </SettingsModalFooter>
    </Modal>
  );
}
